import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import appIcon from '../assets/icon-512.png';

const appConfig = window.appConfig;

const downloadPlatforms = {
  android: {
    name: 'Android',
    buttonText: '下载 Android 版',
    description: '适用于 Android 手机与平板',
    icon: 'fab fa-android',
    button: '下载',
    ...appConfig.android,
  },
  ios: {
    name: 'iPhone / iPad',
    buttonText: '前往 AppStore',
    description: '适用于 iPhone 与 iPad',
    icon: 'fab fa-apple',
    button: 'AppStore',
    ...appConfig.ios,
  },
  windows: {
    name: 'Windows',
    buttonText: '下载 Windows 版',
    description: '适用于 Windows 电脑',
    icon: 'fab fa-windows',
    button: '下载',
    ...appConfig.windows,
  },
  macOS: {
    name: 'macOS',
    buttonText: '下载 macOS 版',
    description: '适用于 Mac 电脑',
    icon: 'fab fa-apple',
    button: '下载',
    ...appConfig.macOS,
  }
};

const platformRows = [
  downloadPlatforms.android,
  downloadPlatforms.ios,
  downloadPlatforms.windows,
  downloadPlatforms.macOS
];

const detectPlatform = () => {
  if (typeof window === 'undefined') return null;

  const userAgent = window.navigator.userAgent || '';
  const platform = window.navigator.userAgentData?.platform || window.navigator.platform || '';
  const isIPadOS = platform === 'MacIntel' && window.navigator.maxTouchPoints > 1;

  if (/Android/i.test(userAgent)) return 'android';
  if (/iPhone|iPad|iPod/i.test(userAgent) || isIPadOS) return 'ios';
  if (/Mac/i.test(platform) || /Macintosh/i.test(userAgent)) return 'macOS';
  if (/Win/i.test(platform) || /Windows/i.test(userAgent)) return 'windows';

  return null;
};

const DownloadIcon = ({ className = '' }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v11m0 0 4-4m-4 4-4-4M5 20h14" />
  </svg>
);

const DownloadApp = () => {
  const [detectedPlatform, setDetectedPlatform] = useState(null);

  useEffect(() => {
    setDetectedPlatform(detectPlatform());
  }, []);

  const primaryPlatform = detectedPlatform ? downloadPlatforms[detectedPlatform] : null;
  const currentVersion = primaryPlatform?.version || appConfig.android.version;

  return (
    <section id="download" className="scroll-mt-20 bg-gray-50 py-16 dark:bg-gray-800 md:py-28">
      <div className="container">
        <motion.div
          className="section-title"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl font-bold text-primary dark:text-white md:text-4xl">下载 Aiabc.ai</h2>
          <p className="mt-4 text-base text-gray-600 dark:text-gray-300 md:text-lg">
            随时随地开始英语自主学习
          </p>
        </motion.div>

        <div className="mt-8 grid overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl shadow-gray-200/50 dark:border-gray-700 dark:bg-gray-900 dark:shadow-black/20 md:mt-12 md:rounded-3xl lg:grid-cols-5">
          <motion.div
            className="relative flex min-h-[420px] flex-col items-center justify-center overflow-hidden bg-gradient-to-br from-[#fff3e8] via-[#fffaf6] to-[#ffd9ba] px-5 py-10 text-center text-primary dark:from-[#321b11] dark:via-gray-900 dark:to-[#421d0e] dark:text-white sm:px-8 sm:py-12 lg:col-span-2 lg:min-h-[520px] lg:py-14"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="absolute -left-28 -top-28 h-72 w-72 rounded-full border border-white/80 bg-white/20 shadow-[0_0_80px_rgba(255,72,0,0.12)] dark:border-white/10 dark:bg-secondary/5" />
            <div className="absolute -bottom-40 -right-28 h-80 w-80 rounded-full border border-white/80 bg-white/20 shadow-[0_0_100px_rgba(255,72,0,0.16)] dark:border-white/10 dark:bg-secondary/5" />
            <div className="absolute left-1/2 top-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-secondary/10 blur-3xl" />

            <div className="relative mb-7">
              <img src={appIcon} alt="Aiabc.ai App 图标" className="h-24 w-24 rounded-2xl sm:h-28 sm:w-28 sm:rounded-3xl md:h-32 md:w-32" />
            </div>

            <h3 className="relative mb-2 text-2xl font-bold sm:text-3xl">Aiabc.ai</h3>
            <p className="relative mb-8 text-sm font-medium text-gray-500 dark:text-gray-400">{currentVersion}</p>

            {primaryPlatform ? (
              <a
                href={primaryPlatform.url}
                target="_blank"
                rel="noreferrer"
                className="relative inline-flex w-full max-w-xs items-center justify-center gap-3 rounded-xl bg-secondary px-4 py-4 text-base font-bold text-white shadow-[0_14px_32px_rgba(255,72,0,0.28)] transition duration-300 hover:-translate-y-1 hover:bg-orange-600 hover:shadow-[0_18px_38px_rgba(255,72,0,0.36)] focus:outline-none focus:ring-4 focus:ring-secondary/30 sm:px-6"
              >
                <i className={`${primaryPlatform.icon} text-xl`} aria-hidden="true" />
                {primaryPlatform.buttonText}
                <DownloadIcon className="h-5 w-5" />
              </a>
            ) : (
              <a
                href="#download-platforms"
                className="relative inline-flex w-full max-w-xs items-center justify-center gap-3 rounded-xl bg-secondary px-4 py-4 text-base font-bold text-white shadow-[0_14px_32px_rgba(255,72,0,0.28)] transition duration-300 hover:-translate-y-1 hover:bg-orange-600 hover:shadow-[0_18px_38px_rgba(255,72,0,0.36)] focus:outline-none focus:ring-4 focus:ring-secondary/30 sm:px-6"
              >
                选择下载平台
                <i className="fas fa-arrow-right" aria-hidden="true" />
              </a>
            )}

            <p className="relative mt-5 text-xs leading-5 text-gray-500 dark:text-gray-400">
              点击后将前往对应平台的官方下载页面
            </p>
          </motion.div>

          <motion.div
            id="download-platforms"
            className="scroll-mt-28 px-4 py-8 sm:px-6 md:px-10 md:py-10 lg:col-span-3"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <div className="mb-4 flex items-end justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wider text-secondary">所有版本</p>
                <h3 className="mt-1 text-xl font-bold text-primary dark:text-white sm:text-2xl">选择下载平台</h3>
              </div>
            </div>

            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {platformRows.map((platform) => {
                const isDetected = primaryPlatform?.name === platform.name;

                return (
                  <a
                    key={platform.name}
                    href={platform.url}
                    target="_blank"
                    rel="noreferrer"
                    className="group grid grid-cols-[3rem_minmax(0,1fr)_1.25rem] items-center gap-x-3 py-5 transition-colors hover:text-secondary sm:flex sm:gap-4"
                  >
                    <span className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-gray-100 text-xl text-primary transition-colors group-hover:bg-secondary/10 group-hover:text-secondary dark:bg-gray-800 dark:text-white">
                      <i className={platform.icon} aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-1 font-bold text-primary transition-colors group-hover:text-secondary dark:text-white">
                        <span className="whitespace-nowrap">{platform.name}</span>
                        <span className="whitespace-nowrap text-[11px] font-medium text-gray-400 dark:text-gray-500 sm:text-xs">{platform.version}</span>
                        {isDetected && (
                          <span className="whitespace-nowrap rounded-full bg-secondary/10 px-2 py-0.5 text-[11px] font-semibold text-secondary sm:text-xs">当前设备</span>
                        )}
                      </span>
                      <span className="mt-1 block text-sm leading-6 text-gray-500 dark:text-gray-400">
                        发布日期：{platform.release} · {platform.description} · {platform.size}
                      </span>
                    </span>
                    <span className="flex flex-none items-center gap-2 font-semibold text-secondary">
                      <span className="hidden sm:inline">{platform.button}</span>
                      <DownloadIcon className="h-5 w-5 transition-transform group-hover:translate-y-0.5" />
                    </span>
                  </a>
                );
              })}

              <div className="grid grid-cols-[3rem_minmax(0,1fr)] items-start gap-x-3 py-5 sm:flex sm:gap-4">
                <span className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-gray-100 text-xl text-primary dark:bg-gray-800 dark:text-white">
                  <i className="fas fa-store" aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-primary dark:text-white">各大应用市场</div>
                  <p className="mt-1 text-sm leading-6 text-gray-500 dark:text-gray-400">
                    Apple App Store、腾讯应用宝、华为、荣耀、小米、OPPO、vivo
                  </p>
                  <div className="mt-3 inline-flex max-w-full flex-wrap items-center gap-2 rounded-lg bg-secondary/10 px-3 py-2 text-sm font-semibold text-secondary">
                    <i className="fas fa-search" aria-hidden="true" />
                    搜索“<span className="select-all">Aiabc.ai</span>”下载更新
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default DownloadApp;
